import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Trash2 } from 'lucide-react';
import { viderVaultAdmin } from '../services/api';
import { usePersonnalisation } from '../hooks/usePersonnalisation';

/**
 * Le bloc « Coffre-fort sécurisé » des Paramètres.
 *
 * Les données du coffre ne sont jamais purgées d'office — même quand l'option
 * n'est plus souscrite, elles attendent une souscription ultérieure. Ce bloc
 * porte le seul effacement possible : à la demande d'un administrateur, mot de
 * passe à l'appui.
 */
export default function VaultAdministration() {
  const perso = usePersonnalisation();
  const queryClient = useQueryClient();
  const [confirmation, setConfirmation] = useState(false);
  const [motDePasse, setMotDePasse] = useState('');
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const vider = useMutation({
    mutationFn: () => viderVaultAdmin(motDePasse),
    onSuccess: (d: any) => {
      setNotice({ type: 'success', message: d.message });
      setConfirmation(false);
      setMotDePasse('');
      queryClient.invalidateQueries({ queryKey: ['vault'] });
      queryClient.invalidateQueries({ queryKey: ['vault-totp'] });
    },
    onError: (e: Error) => setNotice({ type: 'error', message: e.message }),
  });

  const optionOuverte = (perso?.options as any)?.vault !== false;

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Les comptes du coffre — identifiants, mots de passe, codes 2FA — restent chiffrés en
        base quoi qu'il arrive : la fin d'une période offerte ou d'une souscription ferme
        l'écran mais n'efface rien, pour qu'une souscription ultérieure retrouve tout.
      </p>
      {!optionOuverte && (
        <p className="text-sm rounded-xl border border-amber-200 bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200 px-4 py-3">
          Le module Coffre-fort n'est pas souscrit actuellement : les données dorment,
          chiffrées. Vous pouvez le souscrire à nouveau depuis le bloc Modules, ou demander
          l'effacement ci-dessous.
        </p>
      )}

      {!confirmation ? (
        <button
          type="button"
          onClick={() => {
            setNotice(null);
            setConfirmation(true);
          }}
          className="inline-flex items-center gap-2 rounded-xl border border-red-300 dark:border-red-800 px-4 py-2.5 text-sm font-semibold text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
        >
          <Trash2 className="w-4 h-4" /> Vider le coffre…
        </button>
      ) : (
        <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50/60 dark:bg-red-900/10 p-4 space-y-3">
          <p className="text-sm text-red-800 dark:text-red-300 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              <strong>Irréversible :</strong> tous les comptes du coffre seront effacés —
              identifiants, mots de passe et clés 2FA. Confirmez avec votre mot de passe.
            </span>
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="password"
              value={motDePasse}
              onChange={(e) => setMotDePasse(e.target.value)}
              placeholder="Votre mot de passe"
              autoComplete="current-password"
              className="input-field !w-64"
            />
            <button
              type="button"
              onClick={() => vider.mutate()}
              disabled={vider.isPending || motDePasse === ''}
              className="inline-flex items-center gap-2 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-50 px-4 py-2.5 text-sm font-semibold text-white transition-colors"
            >
              {vider.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Effacer définitivement
            </button>
            <button
              type="button"
              onClick={() => {
                setConfirmation(false);
                setMotDePasse('');
              }}
              className="text-sm text-gray-500 dark:text-gray-400 hover:underline"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {notice && (
        <p className={`text-sm ${notice.type === 'success' ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
          {notice.message}
        </p>
      )}
    </div>
  );
}
